import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  TaskReasonSchema,
  TaskTypeSchema,
  type Contact,
  type Lead,
  type Task,
  type User,
} from "@lumika/shared";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
  useZodForm,
} from "@lumika/ui";
import { bdcApi } from "../api/bdc";
import { tasksApi } from "../api/tasks";
import { assignableUsers } from "../lib/user";
import { ContactSearchPicker } from "./ContactSearchPicker";
import { userLabel } from "./MentionedText";
import {
  hydrateMentions,
  MentionTextarea,
  serializeMentions,
  type TrackedMention,
} from "./MentionTextarea";
import { humanize, labelize } from "@lumika/ui";

/**
 * Shared task create/edit dialogs. Used both by the Tasks page (its
 * Kanban board) and the Calendar page, so an operator can manage tasks
 * wherever they see them. Each dialog is self-sufficient — it queries
 * the reference data it needs and invalidates `["tasks"]` on success,
 * so every task list on screen refreshes itself.
 */

/**
 * `conversation.respond` is a system task — a scheduled AI reply — so
 * its raw enum value reads badly. New BDC kinds (Batch C) have
 * display-friendly labels that match the task model in Draft 5.
 */
const TASK_TYPE_LABELS: Record<string, string> = {
  "conversation.respond": "Assistant reply",
  appointment_reminder: "Appt reminder",
  re_engage: "Re-engage",
};
export function taskTypeLabel(taskType: string): string {
  return TASK_TYPE_LABELS[taskType] ?? humanize(taskType);
}

/**
 * Chip color class for a task type — returned as a Tailwind bg/text
 * pair so callers can render a colored chip without branching on types.
 *
 * Matches the BDC design palette:
 *   appointment_reminder → amber warm (pre-appointment reminder)
 *   re_engage            → violet (long-term re-engagement)
 *   (others)             → muted/default
 */
export function taskTypeChipClass(taskType: string): string {
  if (taskType === "appointment_reminder") {
    return "bg-[hsl(var(--triage-warm-bg))] text-[hsl(var(--triage-warm-fg))]";
  }
  if (taskType === "re_engage") {
    return "bg-[hsl(var(--chip-reengage-bg))] text-[hsl(var(--chip-reengage-fg))]";
  }
  return "bg-muted text-muted-foreground";
}

/** ISO timestamp → the `YYYY-MM-DDTHH:mm` a datetime-local input wants. */
function toDateTimeLocal(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

const EditTaskFormSchema = z.object({
  title: z.string().trim().min(1, "Title is required.").max(200),
  description: z.string().trim().max(8000).optional().or(z.literal("")),
  taskType: TaskTypeSchema,
  reason: TaskReasonSchema.or(z.literal("none")),
  dueAt: z.string().optional().or(z.literal("")),
  assignedUserId: z.string().optional().or(z.literal("")),
});
type EditTaskFormValues = z.infer<typeof EditTaskFormSchema>;

/**
 * Full task editor. Every field `UpdateTaskSchema` allows is editable
 * here — title, description, type, reason, due date, assignee — saved
 * together in one click. The task's lifecycle (start / snooze /
 * cancel / complete) lives in its own Actions panel and each fires
 * immediately. A done/cancelled task is read-only — an editable form
 * under a "lifecycle is closed" notice read as a contradiction — with
 * a single Reopen action (the move endpoint allows any-direction
 * transitions and clears completion metadata).
 */
export function TaskEditDialog({
  task,
  users,
  onClose,
}: {
  task: Task;
  users: User[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  // Keep the task's CURRENT assignee selectable even when they no longer
  // qualify (deactivated since the task was filed). Dropping them would
  // leave the Select rendering a blank trigger over a real assignment, and
  // reassigning is the one thing an operator opens this dialog to do.
  const humans = useMemo(() => {
    const assignable = assignableUsers(users);
    const current = task.assignedUserId
      ? users.find((u) => u.id === task.assignedUserId)
      : undefined;
    return current && !assignable.some((u) => u.id === current.id)
      ? [current, ...assignable]
      : assignable;
  }, [users, task.assignedUserId]);
  const [snoozing, setSnoozing] = useState(false);
  const [snoozeAt, setSnoozeAt] = useState("");

  // Hydrate the stored description: `@[id]` tokens → editable `@Name`
  // text, plus the tracked-mention list for re-serialization on save.
  const initialDescription = useMemo(() => {
    const byId = new Map(users.map((u) => [u.id, u]));
    return hydrateMentions(task.description, (id) => {
      const u = byId.get(id);
      return u ? userLabel(u) : undefined;
    });
  }, [task.description, users]);
  const [descriptionMentions, setDescriptionMentions] = useState<
    TrackedMention[]
  >(() => initialDescription.mentions);

  const form = useZodForm(EditTaskFormSchema, {
    title: task.title,
    description: initialDescription.text,
    taskType: task.taskType,
    reason: task.reason ?? "none",
    dueAt: toDateTimeLocal(task.dueAt),
    assignedUserId: task.assignedUserId ?? "",
  });
  const { values, errors, setField } = form;

  // Unsaved edits block the implicit close paths (outside click, Escape);
  // the X and Close buttons still work.
  const dirty =
    values.title !== task.title ||
    (values.description ?? "") !== initialDescription.text ||
    values.taskType !== task.taskType ||
    values.reason !== (task.reason ?? "none") ||
    (values.dueAt ?? "") !== toDateTimeLocal(task.dueAt) ||
    (values.assignedUserId ?? "") !== (task.assignedUserId ?? "") ||
    snoozeAt !== "";

  async function afterMutation(message: string): Promise<void> {
    await qc.invalidateQueries({ queryKey: ["tasks"] });
    toast.success(message);
    onClose();
  }

  const save = useMutation({
    mutationFn: async (input: EditTaskFormValues) => {
      await tasksApi.update(task.id, {
        title: input.title,
        description:
          serializeMentions(
            input.description ?? "",
            descriptionMentions,
          ).trim() || null,
        taskType: input.taskType,
        reason: input.reason === "none" ? null : input.reason,
        dueAt: input.dueAt ? new Date(input.dueAt).toISOString() : null,
      });
      // The assignee has a dedicated endpoint — only call it on a change.
      const nextAssignee = input.assignedUserId || null;
      if (nextAssignee !== (task.assignedUserId ?? null)) {
        await tasksApi.assign(task.id, nextAssignee);
      }
    },
    onSuccess: () => afterMutation("Task saved"),
    onError: (e: Error) => toast.error(e.message),
  });
  const start = useMutation({
    mutationFn: () => tasksApi.start(task.id),
    onSuccess: () => afterMutation("Task marked in progress"),
    onError: (e: Error) => toast.error(e.message),
  });
  const complete = useMutation({
    mutationFn: () => tasksApi.complete(task.id),
    onSuccess: () => afterMutation("Task completed"),
    onError: (e: Error) => toast.error(e.message),
  });
  const cancel = useMutation({
    mutationFn: () => tasksApi.cancel(task.id),
    onSuccess: () => afterMutation("Task cancelled"),
    onError: (e: Error) => toast.error(e.message),
  });
  const snooze = useMutation({
    mutationFn: (availableAt: string) => tasksApi.snooze(task.id, availableAt),
    onSuccess: () => afterMutation("Task snoozed"),
    onError: (e: Error) => toast.error(e.message),
  });
  const reopen = useMutation({
    mutationFn: () => tasksApi.move(task.id, "open"),
    onSuccess: () => afterMutation("Task reopened"),
    onError: (e: Error) => toast.error(e.message),
  });

  const busy =
    save.isPending ||
    start.isPending ||
    complete.isPending ||
    cancel.isPending ||
    snooze.isPending ||
    reopen.isPending;
  const closed = task.status === "done" || task.status === "cancelled";
  const canStart = task.status === "open" || task.status === "snoozed";
  const completedBy = task.completedByUserId
    ? users.find((u) => u.id === task.completedByUserId)
    : null;
  const completedByName = completedBy
    ? (completedBy.displayName ?? completedBy.email ?? "someone")
    : null;

  function submit() {
    const result = form.validate();
    if (!result.ok) return;
    save.mutate(result.data);
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent preventDismiss={dirty}>
        <DialogHeader>
          <DialogTitle>Edit task</DialogTitle>
          <DialogDescription>
            {taskTypeLabel(task.taskType)} · {humanize(task.status)}
          </DialogDescription>
        </DialogHeader>

        <div className="grid max-h-[60vh] gap-4 overflow-y-auto -mx-1 px-1">
          <FormField label="Title" required error={errors.title}>
            <Input
              value={values.title}
              onChange={(e) => setField("title", e.target.value)}
              autoFocus
              disabled={closed}
            />
          </FormField>

          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="Type" error={errors.taskType}>
              <Select
                value={values.taskType}
                onValueChange={(v) =>
                  setField("taskType", v as EditTaskFormValues["taskType"])
                }
                disabled={closed}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* `conversation.respond` is a system type (scheduled AI
                      reply) — only selectable when the task already is one,
                      mirroring the create dialog's filter. */}
                  {TaskTypeSchema.options
                    .filter(
                      (t) =>
                        t !== "conversation.respond" || t === task.taskType,
                    )
                    .map((t) => (
                      <SelectItem key={t} value={t}>
                        {taskTypeLabel(t)}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Reason" error={errors.reason}>
              <Select
                value={values.reason}
                onValueChange={(v) =>
                  setField("reason", v as EditTaskFormValues["reason"])
                }
                disabled={closed}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No reason</SelectItem>
                  {TaskReasonSchema.options.map((r) => (
                    <SelectItem key={r} value={r}>
                      {humanize(r)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Assignee">
              <Select
                value={values.assignedUserId || "__unassigned"}
                onValueChange={(v) =>
                  setField("assignedUserId", v === "__unassigned" ? "" : v)
                }
                disabled={closed}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__unassigned">Unassigned</SelectItem>
                  {humans.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.displayName ?? u.email ?? "—"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Due" optional>
              <Input
                type="datetime-local"
                value={values.dueAt ?? ""}
                onChange={(e) => setField("dueAt", e.target.value)}
                disabled={closed}
              />
            </FormField>
          </div>

          <FormField label="Description" optional error={errors.description}>
            <MentionTextarea
              value={values.description ?? ""}
              onChange={(v) => setField("description", v)}
              mentions={descriptionMentions}
              onMentionsChange={setDescriptionMentions}
              teammates={users}
              rows={3}
              disabled={closed}
            />
          </FormField>

          {closed ? (
            <div className="grid gap-2 rounded-md border bg-muted/30 p-3">
              <p className="text-xs text-muted-foreground">
                This task is {humanize(task.status).toLowerCase()}
                {completedByName ? (
                  <>
                    {" — completed by "}
                    <span className="font-medium text-foreground">
                      {completedByName}
                    </span>
                  </>
                ) : null}
                . Reopen it to make changes.
              </p>
              <div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => reopen.mutate()}
                  disabled={busy}
                >
                  Reopen task
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid gap-2 rounded-md border bg-muted/30 p-3">
              <span className="text-sm font-semibold text-muted-foreground">
                Actions
              </span>
              {snoozing ? (
                <div className="flex flex-wrap items-end gap-2">
                  <FormField label="Snooze until" className="min-w-[12rem] flex-1">
                    <Input
                      type="datetime-local"
                      value={snoozeAt}
                      onChange={(e) => setSnoozeAt(e.target.value)}
                    />
                  </FormField>
                  <Button
                    size="sm"
                    onClick={() =>
                      snoozeAt && snooze.mutate(new Date(snoozeAt).toISOString())
                    }
                    disabled={!snoozeAt || busy}
                  >
                    Snooze
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSnoozing(false)}
                    disabled={busy}
                  >
                    Back
                  </Button>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {canStart && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => start.mutate()}
                      disabled={busy}
                    >
                      Mark in progress
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSnoozing(true)}
                    disabled={busy}
                  >
                    Snooze
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => cancel.mutate()}
                    disabled={busy}
                  >
                    Cancel task
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => complete.mutate()}
                    disabled={busy}
                  >
                    Complete task
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Close
          </Button>
          <Button onClick={submit} disabled={busy || closed}>
            {save.isPending ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const TaskFormSchema = z.object({
  title: z.string().trim().min(1, "Title is required.").max(200),
  description: z.string().trim().max(8000).optional().or(z.literal("")),
  taskType: TaskTypeSchema,
  contactId: z.string().uuid("Pick a contact."),
  leadId: z.string().optional().or(z.literal("")),
  assignedUserId: z.string().optional().or(z.literal("")),
  dueAt: z.string().optional().or(z.literal("")),
});
type TaskFormValues = z.infer<typeof TaskFormSchema>;

/** Same title derivation the contact page's lead list uses. */
function leadLabel(lead: Lead): string {
  const title =
    lead.interest?.trim() || (lead.type ? `${labelize(lead.type)} lead` : "Lead");
  return `${title} · ${labelize(lead.status)}`;
}

/**
 * New-task dialog. A task is always tied to a contact, so the contact
 * picker is required; the rooftop is derived from the chosen contact.
 */
export function NewTaskDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const usersQuery = useQuery({ queryKey: ["bdc", "users"], queryFn: bdcApi.users });
  const humans = assignableUsers(usersQuery.data ?? []);
  // The picked contact — kept so the create mutation can derive its rooftop
  // without loading the full contact list.
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);

  const form = useZodForm(TaskFormSchema, {
    title: "",
    description: "",
    taskType: "other",
    contactId: "",
    leadId: "",
    assignedUserId: "",
    dueAt: "",
  });
  const { values, errors, setField } = form;
  const [descriptionMentions, setDescriptionMentions] = useState<
    TrackedMention[]
  >([]);

  // The picked contact's open leads — lets a manual task carry the same
  // lead link AI-created tasks get (the card's "Open lead" shortcut).
  const leadsQuery = useQuery({
    queryKey: ["bdc", "contact-leads-open", values.contactId],
    queryFn: async () =>
      (
        await bdcApi.leads("false", {
          includeAnonymous: true,
          contactId: values.contactId,
        })
      ).items,
    enabled: values.contactId !== "",
  });
  const contactLeads = leadsQuery.data ?? [];

  // Any input blocks the implicit close paths (outside click, Escape);
  // the X and Cancel buttons still work.
  const dirty =
    values.title !== "" ||
    (values.description ?? "") !== "" ||
    values.contactId !== "" ||
    values.taskType !== "other" ||
    (values.assignedUserId ?? "") !== "" ||
    (values.dueAt ?? "") !== "";

  const create = useMutation({
    mutationFn: (input: TaskFormValues) => {
      const contact =
        selectedContact?.id === input.contactId ? selectedContact : null;
      if (!contact) throw new Error("Selected contact not found.");
      return tasksApi.create({
        rooftopId: null,
        contactId: input.contactId,
        leadId: input.leadId ? input.leadId : null,
        title: input.title,
        description:
          serializeMentions(
            input.description ?? "",
            descriptionMentions,
          ).trim() || null,
        taskType: input.taskType,
        assignedUserId: input.assignedUserId ? input.assignedUserId : null,
        dueAt: input.dueAt ? new Date(input.dueAt).toISOString() : null,
      });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["tasks"] });
      toast.success("Task created");
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function submit() {
    const result = form.validate();
    if (!result.ok) return;
    create.mutate(result.data);
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent preventDismiss={dirty}>
        <DialogHeader>
          <DialogTitle>New task</DialogTitle>
          <DialogDescription>
            A task is always tied to a contact. Assign it now or leave it for the queue.
          </DialogDescription>
        </DialogHeader>

        <div className="grid max-h-[60vh] gap-4 overflow-y-auto -mx-1 px-1">
          <FormField label="Title" required error={errors.title}>
            <Input
              value={values.title}
              onChange={(e) => setField("title", e.target.value)}
              placeholder="e.g. Call back about the trade-in"
              autoFocus
            />
          </FormField>

          <FormField label="Contact" required error={errors.contactId}>
            <ContactSearchPicker
              value={values.contactId}
              onSelect={(contact) => {
                setSelectedContact(contact);
                setField("contactId", contact?.id ?? "");
                // A lead belongs to one contact — re-picking invalidates it.
                setField("leadId", "");
              }}
              placeholder="Search for a contact"
            />
          </FormField>

          {/* Only offered when the contact has open leads — most tasks
              don't need one and the empty picker would just be noise. */}
          {values.contactId !== "" && contactLeads.length > 0 && (
            <FormField label="Lead" optional>
              <Select
                value={values.leadId || "__none"}
                onValueChange={(v) => setField("leadId", v === "__none" ? "" : v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Not linked to a lead</SelectItem>
                  {contactLeads.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {leadLabel(l)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
          )}

          <FormField label="Type" error={errors.taskType}>
            <Select
              value={values.taskType}
              onValueChange={(v) => setField("taskType", v as TaskFormValues["taskType"])}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TaskTypeSchema.options
                  .filter((t) => t !== "conversation.respond")
                  .map((t) => (
                    <SelectItem key={t} value={t}>
                      {taskTypeLabel(t)}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </FormField>

          <FormField label="Assignee" optional>
            <Select
              value={values.assignedUserId || "__unassigned"}
              onValueChange={(v) =>
                setField("assignedUserId", v === "__unassigned" ? "" : v)
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__unassigned">Unassigned</SelectItem>
                {humans.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.displayName ?? u.email ?? "—"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>

          <FormField label="Due" optional>
            <Input
              type="datetime-local"
              value={values.dueAt ?? ""}
              onChange={(e) => setField("dueAt", e.target.value)}
            />
          </FormField>

          <FormField label="Description" optional error={errors.description}>
            <MentionTextarea
              value={values.description ?? ""}
              onChange={(v) => setField("description", v)}
              mentions={descriptionMentions}
              onMentionsChange={setDescriptionMentions}
              teammates={usersQuery.data ?? []}
              rows={3}
            />
          </FormField>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={create.isPending}>
            {create.isPending ? "Creating…" : "Create task"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
